import React, { memo, useMemo, useCallback, ReactElement, FocusEvent, FormEvent } from "react";
import { useFormikContext } from "formik";
import cn from "classnames";
import { type Value } from "react-phone-number-input";
import TextField from "../../TextField/TextField";
import TelephoneField from "../../TelephoneField/TelephoneField";
import { getScreenByStep, impressionBase } from "../constants";
import styles from "./Contact.module.scss";

/**
 * Renders primary screen for collecting user contact data. It renders a form with fields for the user's first name,
 * last name, phone number, and email address. The objective of the function is to provide a user-friendly interface
 * for the user to input their contact information and validate the input data.
 *
 * @returns {ReactElement} The Contact screen.
 */
const Contact = memo((props: { enabled: boolean }): ReactElement => {
    const { enabled = false } = props;
    const { errors, values, handleChange, setFieldTouched, setFieldValue, touched } = useFormikContext();

    // Convenient wrappers to get correct typings for these objects that have undefined keys.
    const errorObj = useMemo(() => (errors as Record<string, string>), [errors]);
    const valueObj = useMemo(() => (values as Record<string, string>), [values]);
    const touchedObj = useMemo(() => (touched as Record<string, string>), [touched]);
    const screen = getScreenByStep(2);
    const impression = {
        ...impressionBase,
        sp_modal_screen_name: screen.name,
        sp_modal_screen_order: screen.step,
    };

    const onBlur = useCallback((e: FocusEvent<HTMLInputElement>): void => {
        const fieldName = (e.target as HTMLInputElement).name;
        if (!touchedObj[fieldName]) {
            setFieldTouched(fieldName);
        }
        handleChange(e);
    }, [handleChange, setFieldTouched, touchedObj]);

    const onChange = useCallback((e: FormEvent<HTMLInputElement>): void => {
        const fieldName = (e.target as HTMLInputElement).name;
        if (touchedObj[fieldName]) {
            handleChange(e);
        }
    }, [handleChange, touchedObj]);

    const onPhoneOnChange = useCallback((value?: string): void => {
        setFieldValue("phone", value);
    }, [setFieldValue]);

    const onPhoneOnBlur = useCallback((): void => {
        if (!touchedObj.phone) {
            setFieldTouched("phone");
        }
    }, [setFieldTouched, touchedObj.phone]);

    return (
        <div className={cn(styles.contact, enabled ? styles.enabled : "")}
            data-tracking-props={JSON.stringify(impression)}
            data-tracking-event-type="impression"
        >
            <p className={styles.screenHeading}>Contact info</p>
            <div className={styles.row}>
            <TextField  {...{
                    type: "text",
                    name: "firstName",
                    id: "firstName",
                    label: "First Name",
                    required: true,
                    autoComplete: "given-name",
                    onBlur,
                    onChange,
                    autoCapitalize: "true",
                    defaultValue: valueObj?.firstName ?? "",
                    fieldErrors: touchedObj.firstName ? [errorObj?.firstName].filter(Boolean) : [],
                    labelClassName: styles.label,
                }} />
                <TextField {...{
                    type: "text",
                    name: "lastName",
                    id: "lastName",
                    label: "Last Name",
                    required: true,
                    autoComplete: "family-name",
                    onBlur,
                    onChange,
                    autoCapitalize: "true",
                    defaultValue: valueObj?.lastName ?? "",
                    fieldErrors: touchedObj.lastName ? [errorObj?.lastName].filter(Boolean) : [],
                    labelClassName: styles.label,
                }} />
            </div>
            <div className={styles.row}>
                <TelephoneField {...{
                    label: "Phone",
                    name: "phone",
                    id: "phone",
                    // This onChange is different than others due to the phone library returning a value, not an event.
                    onChange: onPhoneOnChange,
                    onBlur: onPhoneOnBlur,
                    defaultValue: valueObj?.phone as Value,
                    fieldErrors: touchedObj.phone ? [errorObj?.phone].filter(Boolean) : [],
                    required: true
                }} />
                <span></span>
            </div>
        </div>
    );
});
Contact.displayName = "Contact";

export default Contact;
